import React, { useState, useEffect, useRef } from 'react';
import { Plus, ChevronLeft, ChevronRight, Minus, Phone, Mail, Clock, Building, Users, User, Contact, Settings, X, CheckCircle, AlertCircle, FileText, Award, Shield, Activity } from 'lucide-react';
import apiClient from '../../../services/apiClient';
import { doctorService } from '../services/doctorService';
import { patientService } from '../../patient/services/patientService';

const AdminDoctorsPage = () => {
  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Admin Doctors';
  }, []);

  // State for doctor data
  const [doctorsData, setDoctorsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedDoctor, setExpandedDoctor] = useState(null);

  // Toast notification state
  const [toasts, setToasts] = useState([]);
  
  // Edit state management
  const [editingSection, setEditingSection] = useState(null);
  const [editingDoctorId, setEditingDoctorId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);

  // Add Doctor Modal State
  const [showAddModal, setShowAddModal] = useState(false);
  const [addDoctorData, setAddDoctorData] = useState({
    firstname: '',
    lastname: '',
    doctor_address: '',
    suburb: '',
    state: '',
    postcode: '',
    country: '',
    phone: '',
    email: '',
    doctor_phone_number: '',
    specialties: '',
    summary: '',
    accounts_email: '',
    hourly_rate: '',
    appt_rate: '',
    is_active: true,
    date_of_birth: '',
    gender: '',
    languages: '',
    license_number: '',
    years_experience: '',
    verification_status: 'pending'
  });
  const [addLoading, setAddLoading] = useState(false);

  // State for filters
  const [searchTerm, setSearchTerm] = useState('');
  const [includeInactive, setIncludeInactive] = useState('');
  const [filterByCountry, setFilterByCountry] = useState('');
  const [filterByState, setFilterByState] = useState('');
  const [filterBySuburb, setFilterBySuburb] = useState('');
  const [filterBySpecialty, setFilterBySpecialty] = useState('');
  const [sortBy, setSortBy] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Dropdown states
  const [showIncludeInactiveDropdown, setShowIncludeInactiveDropdown] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [showSuburbDropdown, setShowSuburbDropdown] = useState(false);
  const [showSpecialtyDropdown, setShowSpecialtyDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  // Refs for dropdowns
  const includeInactiveRef = useRef(null);
  const countryRef = useRef(null);
  const stateRef = useRef(null);
  const suburbRef = useRef(null);
  const specialtyRef = useRef(null);
  const sortRef = useRef(null);

  // Fetch doctors data
  const fetchDoctors = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // CORS test removed - actual API calls work fine

      // Check if user is authenticated
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.warn('⚠️ No authentication token found');
        setError('Please log in to view doctors. No authentication token found.');
        return;
      }

      // Try the dedicated doctor service with proper error handling
      try {
        console.log('🩺 Attempting to fetch doctors via doctorService...');
        const data = await doctorService.getAllDoctors();
        
        if (!data || data.length === 0) {
          console.warn('⚠️ No doctors found, this might be due to missing database migration');
          setError('No doctors found. You may need to run the database migration first. Check the console for details.');
          return;
        }
        
        setDoctorsData(data);
        console.log('✅ Successfully fetched doctors via doctorService');
        return;
      } catch (doctorServiceError) {
        console.warn('⚠️ Doctor service failed:', doctorServiceError);
        
        if (doctorServiceError.message.includes('401') || doctorServiceError.message.includes('Unauthorized')) {
          setError('Authentication failed. Please log in again.');
          return;
        }
        
        // Fallback to patient service
        try {
          console.log('🔄 Trying patientService as fallback...');
          const data = await patientService.getAllDoctors();
          setDoctorsData(data);
          console.log('✅ Successfully fetched doctors via patientService fallback');
        } catch (fallbackError) {
          console.error('❌ Fallback also failed:', fallbackError);
          if (fallbackError.message.includes('401') || fallbackError.message.includes('Unauthorized')) {
            setError('Authentication failed. Please log in again.');
          } else {
            setError('Failed to load doctors. Please check your connection and try again.');
          }
        }
      }
    } catch (err) {
      setError('Failed to load doctors');
      console.error('❌ Unexpected error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch doctors on component mount
  useEffect(() => {
    fetchDoctors();
  }, []);

  // Helper functions to get unique values for dropdowns
  const getUniqueStates = React.useMemo(() => {
    return [...new Set(doctorsData.map(doctor => doctor.state))].filter(Boolean).sort();
  }, [doctorsData]);

  const getUniqueSuburbs = React.useMemo(() => {
    return [...new Set(doctorsData.map(doctor => doctor.suburb))].filter(Boolean).sort();
  }, [doctorsData]);

  const getUniqueCountries = React.useMemo(() => {
    return [...new Set(doctorsData.map(doctor => doctor.country))].filter(Boolean).sort();
  }, [doctorsData]);

  const getUniqueSpecialties = React.useMemo(() => {
    const specialtiesSet = new Set();
    doctorsData.forEach(doctor => {
      if (doctor.specialties) {
        doctor.specialties.split(',').forEach(specialty => {
          specialtiesSet.add(specialty.trim());
        });
      }
    });
    return [...specialtiesSet].sort();
  }, [doctorsData]);

  // Toast notification functions
  const addToast = (message, type = 'success') => {
    const id = Date.now();
    const newToast = { id, message, type };
    setToasts(prev => [...prev, newToast]);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      removeToast(id);
    }, 5000);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Filter and search logic
  const filteredDoctors = React.useMemo(() => {
    let filtered = [...doctorsData];

    // Search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(doctor => 
        doctor.firstname?.toLowerCase().includes(searchLower) ||
        doctor.lastname?.toLowerCase().includes(searchLower) ||
        doctor.doctor_id?.toLowerCase().includes(searchLower) ||
        doctor.specialties?.toLowerCase().includes(searchLower) ||
        doctor.email?.toLowerCase().includes(searchLower)
      );
    }

    // Include inactive filter
    if (includeInactive === 'No') {
      filtered = filtered.filter(doctor => doctor.is_active !== false);
    } else if (includeInactive === 'Yes') {
      // Show all (no additional filtering)
    }

    // Country filter
    if (filterByCountry) {
      filtered = filtered.filter(doctor => 
        doctor.country?.toLowerCase().includes(filterByCountry.toLowerCase())
      );
    }

    // State filter
    if (filterByState) {
      filtered = filtered.filter(doctor => 
        doctor.state?.toLowerCase().includes(filterByState.toLowerCase())
      );
    }

    // Suburb filter
    if (filterBySuburb) {
      filtered = filtered.filter(doctor => 
        doctor.suburb?.toLowerCase().includes(filterBySuburb.toLowerCase())
      );
    }

    // Specialty filter
    if (filterBySpecialty) {
      filtered = filtered.filter(doctor => 
        doctor.specialties?.toLowerCase().includes(filterBySpecialty.toLowerCase())
      );
    }

    // Sort filter
    if (sortBy) {
      filtered.sort((a, b) => {
        switch (sortBy) {
          case 'Name (A-Z)':
            return (a.firstname || '').localeCompare(b.firstname || '');
          case 'Name (Z-A)':
            return (b.firstname || '').localeCompare(a.firstname || '');
          case 'Specialty':
            return (a.specialties || '').localeCompare(b.specialties || '');
          case 'Date Added':
            return new Date(b.created_at || 0) - new Date(a.created_at || 0);
          case 'Status':
            return (b.is_active ? 1 : 0) - (a.is_active ? 1 : 0);
          default:
            return 0;
        }
      });
    }

    return filtered;
  }, [doctorsData, searchTerm, includeInactive, filterByCountry, filterByState, filterBySuburb, filterBySpecialty, sortBy]);

  // Pagination logic with filtered results
  const totalPages = Math.ceil(filteredDoctors.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentDoctors = filteredDoctors.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, includeInactive, filterByCountry, filterByState, filterBySuburb, filterBySpecialty, sortBy]);

  // Handle dropdown toggles
  const toggleIncludeInactiveDropdown = () => {
    setShowIncludeInactiveDropdown(!showIncludeInactiveDropdown);
    setShowCountryDropdown(false);
    setShowStateDropdown(false);
    setShowSuburbDropdown(false);
    setShowSpecialtyDropdown(false);
    setShowSortDropdown(false);
  };

  const toggleCountryDropdown = () => {
    setShowCountryDropdown(!showCountryDropdown);
    setShowIncludeInactiveDropdown(false);
    setShowStateDropdown(false);
    setShowSuburbDropdown(false);
    setShowSpecialtyDropdown(false);
    setShowSortDropdown(false);
  };

  const toggleStateDropdown = () => {
    setShowStateDropdown(!showStateDropdown);
    setShowIncludeInactiveDropdown(false);
    setShowCountryDropdown(false);
    setShowSuburbDropdown(false);
    setShowSpecialtyDropdown(false);
    setShowSortDropdown(false);
  };

  const toggleSuburbDropdown = () => {
    setShowSuburbDropdown(!showSuburbDropdown);
    setShowIncludeInactiveDropdown(false);
    setShowCountryDropdown(false);
    setShowStateDropdown(false);
    setShowSpecialtyDropdown(false);
    setShowSortDropdown(false);
  };

  const toggleSpecialtyDropdown = () => {
    setShowSpecialtyDropdown(!showSpecialtyDropdown);
    setShowIncludeInactiveDropdown(false);
    setShowCountryDropdown(false);
    setShowStateDropdown(false);
    setShowSuburbDropdown(false);
    setShowSortDropdown(false);
  };

  const toggleSortDropdown = () => {
    setShowSortDropdown(!showSortDropdown);
    setShowIncludeInactiveDropdown(false);
    setShowCountryDropdown(false);
    setShowStateDropdown(false);
    setShowSuburbDropdown(false);
    setShowSpecialtyDropdown(false);
  };

  // Handle dropdown selections
  const handleIncludeInactiveSelect = (option) => {
    setIncludeInactive(option);
    setShowIncludeInactiveDropdown(false);
  };

  const handleCountrySelect = (country) => {
    setFilterByCountry(country);
    setShowCountryDropdown(false);
  };

  const handleStateSelect = (state) => {
    setFilterByState(state);
    setShowStateDropdown(false);
  };

  const handleSuburbSelect = (suburb) => {
    setFilterBySuburb(suburb);
    setShowSuburbDropdown(false);
  };

  const handleSpecialtySelect = (specialty) => {
    setFilterBySpecialty(specialty);
    setShowSpecialtyDropdown(false);
  };

  const handleSortSelect = (sortOption) => {
    setSortBy(sortOption);
    setShowSortDropdown(false);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setIncludeInactive('');
    setFilterByCountry('');
    setFilterByState('');
    setFilterBySuburb('');
    setFilterBySpecialty('');
    setSortBy('');
    setCurrentPage(1);
  };

  // Doctor expansion toggle
  const toggleDoctorExpansion = (doctorId) => {
    setExpandedDoctor(expandedDoctor === doctorId ? null : doctorId);
    // Cancel any editing when expanding/collapsing
    cancelEdit();
  };

  // Edit functionality
  const startEdit = (section, doctorId) => {
    const doctor = doctorsData.find(d => d.id === doctorId);
    if (doctor) {
      setEditingSection(section);
      setEditingDoctorId(doctorId);
      setEditFormData(prepareEditData(doctor, section));
    }
  };

  const cancelEdit = () => {
    setEditingSection(null);
    setEditingDoctorId(null);
    setEditFormData({});
  };

  const prepareEditData = (doctor, section) => {
    switch (section) {
      case 'doctor-details':
        return {
          firstname: doctor.firstname || '',
          lastname: doctor.lastname || '',
          doctor_address: doctor.doctor_address || '',
          suburb: doctor.suburb || '',
          state: doctor.state || '',
          postcode: doctor.postcode || '',
          country: doctor.country || '',
          phone: doctor.phone || '',
          email: doctor.email || '',
          specialties: doctor.specialties || '',
          summary: doctor.summary || ''
        };
      case 'profile':
        return {
          summary: doctor.summary || '',
          date_of_birth: doctor.date_of_birth || '',
          gender: doctor.gender || '',
          languages: doctor.languages || '',
          license_number: doctor.license_number || '',
          years_experience: doctor.years_experience || '',
          verification_status: doctor.verification_status || 'pending'
        };
      case 'specialties':
        return {
          specialties: doctor.specialties || ''
        };
      case 'accounts':
        return {
          accounts_email: doctor.accounts_email || '',
          hourly_rate: doctor.hourly_rate || '',
          appt_rate: doctor.appt_rate || ''
        };
      default:
        return {};
    }
  };

  const updateEditFormData = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveChanges = async () => {
    if (!editingDoctorId || !editingSection) return;

    try {
      setSaveLoading(true);
      
      // Update doctor details via API
      const updatedDoctor = await doctorService.updateDoctor(editingDoctorId, editFormData);
      
      // Update local state
      setDoctorsData(prevDoctors => 
        prevDoctors.map(doctor => 
          doctor.id === editingDoctorId ? { ...doctor, ...updatedDoctor } : doctor
        )
      );

      // Exit edit mode
      cancelEdit();

      // Show success message
      addToast('Doctor details updated successfully!', 'success');
      
    } catch (error) {
      console.error('Error saving changes:', error);
      addToast(`Failed to save changes: ${error.message}`, 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  // Add doctor modal functions
  const openAddModal = () => {
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setAddDoctorData({
      firstname: '',
      lastname: '',
      doctor_address: '',
      suburb: '',
      state: '',
      postcode: '',
      country: '',
      phone: '',
      email: '',
      doctor_phone_number: '',
      specialties: '',
      summary: '',
      accounts_email: '',
      hourly_rate: '',
      appt_rate: '',
      is_active: true,
      date_of_birth: '',
      gender: '',
      languages: '',
      license_number: '',
      years_experience: '',
      verification_status: 'pending'
    });
  };

  const updateAddDoctorData = (field, value) => {
    setAddDoctorData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddDoctor = async () => {
    try {
      setAddLoading(true);
      
      // Validate required fields
      if (!addDoctorData.email) {
        addToast('Email is required to create a doctor account.', 'error');
        return;
      }
      
      if (!addDoctorData.firstname || !addDoctorData.lastname) {
        addToast('First name and last name are required.', 'error');
        return;
      }
      
      // Create new doctor with user account via enhanced API
      const newDoctor = await doctorService.createDoctorWithUser(addDoctorData);
      
      // Add the new doctor to the local state
      setDoctorsData(prevDoctors => [newDoctor, ...prevDoctors]);
      
      // Close modal and reset form
      closeAddModal();
      
      // Show success message
      addToast('Doctor created successfully! A user account has been created and credentials have been sent to the admin email.', 'success');
      
    } catch (error) {
      console.error('Error creating doctor:', error);
      
      // Check for specific error messages
      if (error.message.includes('User already exists with email')) {
        addToast('A user with this email already exists. Please use a different email address.', 'error');
      } else {
        addToast(`Failed to create doctor: ${error.message}`, 'error');
      }
    } finally {
      setAddLoading(false);
    }
  };

  // Handle pagination
  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (includeInactiveRef.current && !includeInactiveRef.current.contains(event.target)) {
        setShowIncludeInactiveDropdown(false);
      }
      if (countryRef.current && !countryRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
      }
      if (stateRef.current && !stateRef.current.contains(event.target)) {
        setShowStateDropdown(false);
      }
      if (suburbRef.current && !suburbRef.current.contains(event.target)) {
        setShowSuburbDropdown(false);
      }
      if (specialtyRef.current && !specialtyRef.current.contains(event.target)) {
        setShowSpecialtyDropdown(false);
      }
      if (sortRef.current && !sortRef.current.contains(event.target)) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Dummy qualifications data
  const getDummyQualifications = (doctorId) => {
    const qualifications = [
      { id: 1, name: 'Medical_Degree.pdf', type: 'Medical Degree', icon: Award },
      { id: 2, name: 'Board_Certification.pdf', type: 'Board Certification', icon: Shield },
      { id: 3, name: 'Medical_License.pdf', type: 'Medical License', icon: FileText },
      { id: 4, name: 'Continuing_Education.pdf', type: 'Continuing Education', icon: FileText },
      { id: 5, name: 'Specialty_Certificate.pdf', type: 'Specialty Certificate', icon: Award }
    ];
    
    // Return 3-5 qualifications per doctor (varies by doctor ID)
    const count = 3 + (doctorId % 3);
    return qualifications.slice(0, count);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading doctors...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={fetchDoctors}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Blue header */}
      <div className="bg-blue-600 text-white py-1 px-2">
        <h1 className="text-xl font-bold text-center">Doctors</h1>
      </div>

      <div className="flex">
        {/* Left Column - Filters (25%) */}
        <div className="w-1/4 bg-gray-50 min-h-screen p-4">
          <div className="bg-white rounded-lg border border-blue-200 p-3 mt-3">
            <div className="space-y-3">
              {/* Add Doctor Button */}
              <div>
                <button
                  onClick={openAddModal}
                  className="w-full px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm flex items-center justify-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Doctor
                </button>
              </div>

              {/* Search */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Search:
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search"
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* Include Inactive */}
              <div className="relative" ref={includeInactiveRef}>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Include Inactive:
                </label>
                <div 
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white cursor-pointer flex justify-between items-center hover:bg-gray-50"
                  onClick={toggleIncludeInactiveDropdown}
                >
                  <span className={includeInactive ? 'text-gray-900' : 'text-gray-500'}>
                    {includeInactive || 'Yes / No'}
                  </span>
                  <svg className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {showIncludeInactiveDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg">
                    {['Yes', 'No'].map((option) => (
                      <div
                        key={option}
                        className="px-2 py-1 text-xs cursor-pointer hover:bg-blue-100"
                        onClick={() => handleIncludeInactiveSelect(option)}
                      >
                        {option}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Filter By Country */}
              <div className="relative" ref={countryRef}>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Filter By Country:
                </label>
                <div 
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white cursor-pointer flex justify-between items-center hover:bg-gray-50"
                  onClick={toggleCountryDropdown}
                >
                  <span className={filterByCountry ? 'text-gray-900' : 'text-gray-500'}>
                    {filterByCountry || 'Please Select'}
                  </span>
                  <svg className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {showCountryDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-32 overflow-y-auto">
                    {getUniqueCountries.map((country) => (
                      <div
                        key={country}
                        className="px-2 py-1 text-xs cursor-pointer hover:bg-blue-100"
                        onClick={() => handleCountrySelect(country)}
                      >
                        {country}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Filter By State */}
              <div className="relative" ref={stateRef}>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Filter By State:
                </label>
                <div 
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white cursor-pointer flex justify-between items-center hover:bg-gray-50"
                  onClick={toggleStateDropdown}
                >
                  <span className={filterByState ? 'text-gray-900' : 'text-gray-500'}>
                    {filterByState || 'Please Select'}
                  </span>
                  <svg className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {showStateDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-32 overflow-y-auto">
                  {getUniqueStates.map((state) => (
                    <div
                      key={state}
                      className="px-2 py-1 text-xs cursor-pointer hover:bg-blue-100"
                      onClick={() => handleStateSelect(state)}
                    >
                      {state}
                    </div>
                  ))}
                </div>
                )}
              </div>

              {/* Filter By Suburb */}
              <div className="relative" ref={suburbRef}>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Filter By Suburb:
                </label>
                <div 
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white cursor-pointer flex justify-between items-center hover:bg-gray-50"
                  onClick={toggleSuburbDropdown}
                >
                  <span className={filterBySuburb ? 'text-gray-900' : 'text-gray-500'}>
                    {filterBySuburb || 'Please Select'}
                  </span>
                  <svg className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {showSuburbDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-32 overflow-y-auto">
                    {getUniqueSuburbs.map((suburb) => (
                      <div
                        key={suburb}
                        className="px-2 py-1 text-xs cursor-pointer hover:bg-blue-100"
                        onClick={() => handleSuburbSelect(suburb)}
                      >
                        {suburb}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Filter By Specialty */}
              <div className="relative" ref={specialtyRef}>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Filter By Specialty:
                </label>
                <div 
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white cursor-pointer flex justify-between items-center hover:bg-gray-50"
                  onClick={toggleSpecialtyDropdown}
                >
                  <span className={filterBySpecialty ? 'text-gray-900' : 'text-gray-500'}>
                    {filterBySpecialty || 'Please Select'}
                  </span>
                  <svg className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {showSpecialtyDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-32 overflow-y-auto">
                    {getUniqueSpecialties.map((specialty) => (
                      <div
                        key={specialty}
                        className="px-2 py-1 text-xs cursor-pointer hover:bg-blue-100"
                        onClick={() => handleSpecialtySelect(specialty)}
                      >
                        {specialty}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Sort By */}
              <div className="relative" ref={sortRef}>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Sort By:
                </label>
                <div 
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white cursor-pointer flex justify-between items-center hover:bg-gray-50"
                  onClick={toggleSortDropdown}
                >
                  <span className={sortBy ? 'text-gray-900' : 'text-gray-500'}>
                    {sortBy || 'Sort Options'}
                  </span>
                  <svg className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {showSortDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg">
                    {['Name (A-Z)', 'Name (Z-A)', 'Specialty', 'Date Added', 'Status'].map((option) => (
                      <div
                        key={option}
                        className="px-2 py-1 text-xs cursor-pointer hover:bg-blue-100"
                        onClick={() => handleSortSelect(option)}
                      >
                        {option}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Reset Filters Button */}
              <div className="flex justify-end">
                <button
                  onClick={resetFilters}
                  className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-xs flex items-center gap-1"
                >
                  <X className="h-3 w-3" />
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Doctor List (75%) */}
        <div className="w-3/4 bg-gray-50 p-6">
          <div className="bg-white rounded-lg shadow-md">
            <div className="p-4">
              <div className="space-y-1">
                {currentDoctors.map((doctor) => (
                  <div key={doctor.id} className="border border-gray-200 rounded-md">
                    <div className="flex justify-between items-center p-3">
                      <div className="flex items-center flex-1">
                        <User className="h-4 w-4 mr-2 text-blue-600" />
                        <span className="text-sm font-medium text-gray-900">
                          {doctor.firstname && doctor.lastname ? `${doctor.firstname} ${doctor.lastname}` : 'Unknown Doctor'}
                        </span>
                        {doctor.is_active === false && (
                          <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-800 rounded">
                            Inactive
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => toggleDoctorExpansion(doctor.id)}
                        className={`w-7 h-7 rounded-md transition-colors flex items-center justify-center ${
                          expandedDoctor === doctor.id
                            ? 'bg-blue-600 text-white'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {expandedDoctor === doctor.id ? (
                          <Minus className="h-3 w-3" />
                        ) : (
                          <Plus className="h-3 w-3" />
                        )}
                      </button>
                    </div>

                    {/* Expanded Doctor Details */}
                    {expandedDoctor === doctor.id && (
                      <div className="bg-gray-100 p-4 space-y-4">
                        {/* Doctor Details Section */}
                        <div className="bg-white rounded-lg border border-gray-200">
                          <div className={`px-3 py-1.5 flex justify-between items-center ${
                            expandedDoctor === doctor.id 
                              ? 'bg-gradient-to-r from-blue-50 to-indigo-100 border-b border-blue-200 text-blue-900'
                              : 'bg-blue-600 text-white'
                          }`}>
                            <h3 className="text-xs font-medium flex items-center">
                              <User className="h-3 w-3 mr-2" />
                              Doctor Details
                            </h3>
                            {editingSection === 'doctor-details' && editingDoctorId === doctor.id ? (
                              <div className="flex gap-2">
                                <button
                                  onClick={saveChanges}
                                  disabled={saveLoading}
                                  className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"
                                >
                                  {saveLoading ? 'Saving...' : 'Save'}
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => startEdit('doctor-details', doctor.id)}
                                className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                              >
                                Edit Record
                              </button>
                            )}
                          </div>
                          <div className="p-3">
                            {/* First Row */}
                            <div className="grid grid-cols-5 gap-2 mb-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">First Name</label>
                                <input
                                  type="text"
                                  value={
                                    editingSection === 'doctor-details' && editingDoctorId === doctor.id
                                      ? editFormData.firstname || ''
                                      : doctor.firstname || ''
                                  }
                                  onChange={(e) => editingSection === 'doctor-details' && editingDoctorId === doctor.id && updateEditFormData('firstname', e.target.value)}
                                  readOnly={!(editingSection === 'doctor-details' && editingDoctorId === doctor.id)}
                                  className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                    editingSection === 'doctor-details' && editingDoctorId === doctor.id
                                      ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                      : 'bg-gray-50'
                                  }`}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Last Name</label>
                                <input
                                  type="text"
                                  value={
                                    editingSection === 'doctor-details' && editingDoctorId === doctor.id
                                      ? editFormData.lastname || ''
                                      : doctor.lastname || ''
                                  }
                                  onChange={(e) => editingSection === 'doctor-details' && editingDoctorId === doctor.id && updateEditFormData('lastname', e.target.value)}
                                  readOnly={!(editingSection === 'doctor-details' && editingDoctorId === doctor.id)}
                                  className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                    editingSection === 'doctor-details' && editingDoctorId === doctor.id
                                      ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                      : 'bg-gray-50'
                                  }`}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Doctor Address</label>
                                <input
                                  type="text"
                                  value={
                                    editingSection === 'doctor-details' && editingDoctorId === doctor.id
                                      ? editFormData.doctor_address || ''
                                      : doctor.doctor_address || ''
                                  }
                                  onChange={(e) => editingSection === 'doctor-details' && editingDoctorId === doctor.id && updateEditFormData('doctor_address', e.target.value)}
                                  readOnly={!(editingSection === 'doctor-details' && editingDoctorId === doctor.id)}
                                  className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                    editingSection === 'doctor-details' && editingDoctorId === doctor.id
                                      ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                      : 'bg-gray-50'
                                  }`}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Suburb</label>
                                {editingSection === 'doctor-details' && editingDoctorId === doctor.id ? (
                                  <>
                                    <input
                                      type="text"
                                      list={`suburb-list-${doctor.id}`}
                                      value={editFormData.suburb || ''}
                                      onChange={(e) => updateEditFormData('suburb', e.target.value)}
                                      placeholder="Type or select suburb"
                                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                    <datalist id={`suburb-list-${doctor.id}`}>
                                      {getUniqueSuburbs.map((suburb) => (
                                        <option key={suburb} value={suburb} />
                                      ))}
                                    </datalist>
                                  </>
                                ) : (
                                  <input
                                    type="text"
                                    value={doctor.suburb || ''}
                                    readOnly
                                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-gray-50"
                                  />
                                )}
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">State</label>
                                {editingSection === 'doctor-details' && editingDoctorId === doctor.id ? (
                                  <>
                                    <input
                                      type="text"
                                      list={`state-list-${doctor.id}`}
                                      value={editFormData.state || ''}
                                      onChange={(e) => updateEditFormData('state', e.target.value)}
                                      placeholder="Type or select state"
                                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                    <datalist id={`state-list-${doctor.id}`}>
                                      {getUniqueStates.map((state) => (
                                        <option key={state} value={state} />
                                      ))}
                                    </datalist>
                                  </>
                                ) : (
                                  <input
                                    type="text"
                                    value={doctor.state || ''}
                                    readOnly
                                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-gray-50"
                                  />
                                )}
                              </div>
                            </div>

                            {/* Second Row */}
                            <div className="grid grid-cols-5 gap-2">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Postcode</label>
                                <input
                                  type="text"
                                  value={
                                    editingSection === 'doctor-details' && editingDoctorId === doctor.id
                                      ? editFormData.postcode || ''
                                      : doctor.postcode || ''
                                  }
                                  onChange={(e) => editingSection === 'doctor-details' && editingDoctorId === doctor.id && updateEditFormData('postcode', e.target.value)}
                                  readOnly={!(editingSection === 'doctor-details' && editingDoctorId === doctor.id)}
                                  className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                    editingSection === 'doctor-details' && editingDoctorId === doctor.id
                                      ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                      : 'bg-gray-50'
                                  }`}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Country</label>
                                {editingSection === 'doctor-details' && editingDoctorId === doctor.id ? (
                                  <>
                                    <input
                                      type="text"
                                      list={`country-list-${doctor.id}`}
                                      value={editFormData.country || ''}
                                      onChange={(e) => updateEditFormData('country', e.target.value)}
                                      placeholder="Type or select country"
                                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                    <datalist id={`country-list-${doctor.id}`}>
                                      {getUniqueCountries.map((country) => (
                                        <option key={country} value={country} />
                                      ))}
                                    </datalist>
                                  </>
                                ) : (
                                  <input
                                    type="text"
                                    value={doctor.country || ''}
                                    readOnly
                                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-gray-50"
                                  />
                                )}
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Doctor Phone</label>
                                <input
                                  type="text"
                                  value={
                                    editingSection === 'doctor-details' && editingDoctorId === doctor.id
                                      ? editFormData.phone || ''
                                      : doctor.phone || ''
                                  }
                                  onChange={(e) => editingSection === 'doctor-details' && editingDoctorId === doctor.id && updateEditFormData('phone', e.target.value)}
                                  readOnly={!(editingSection === 'doctor-details' && editingDoctorId === doctor.id)}
                                  className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                    editingSection === 'doctor-details' && editingDoctorId === doctor.id
                                      ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                      : 'bg-gray-50'
                                  }`}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Doctor Email</label>
                                <input
                                  type="email"
                                  value={
                                    editingSection === 'doctor-details' && editingDoctorId === doctor.id
                                      ? editFormData.email || ''
                                      : doctor.email || ''
                                  }
                                  onChange={(e) => editingSection === 'doctor-details' && editingDoctorId === doctor.id && updateEditFormData('email', e.target.value)}
                                  readOnly={!(editingSection === 'doctor-details' && editingDoctorId === doctor.id)}
                                  className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                    editingSection === 'doctor-details' && editingDoctorId === doctor.id
                                      ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                      : 'bg-gray-50'
                                  }`}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Specialties</label>
                                <input
                                  type="text"
                                  value={
                                    editingSection === 'doctor-details' && editingDoctorId === doctor.id
                                      ? editFormData.specialties || ''
                                      : doctor.specialties || ''
                                  }
                                  onChange={(e) => editingSection === 'doctor-details' && editingDoctorId === doctor.id && updateEditFormData('specialties', e.target.value)}
                                  readOnly={!(editingSection === 'doctor-details' && editingDoctorId === doctor.id)}
                                  className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                    editingSection === 'doctor-details' && editingDoctorId === doctor.id
                                      ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                      : 'bg-gray-50'
                                  }`}
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Specialties Section */}
                        <div className="bg-white rounded-lg border border-gray-200">
                          <div className={`px-3 py-1.5 flex justify-between items-center ${
                            expandedDoctor === doctor.id 
                              ? 'bg-gradient-to-r from-blue-600 to-blue-700' 
                              : 'bg-blue-600'
                          } text-white`}>
                            <h3 className="text-xs font-medium flex items-center">
                              <Award className="h-3 w-3 mr-2" />
                              Specialties
                            </h3>
                            {editingSection === 'specialties' && editingDoctorId === doctor.id ? (
                              <div className="flex gap-2">
                                <button
                                  onClick={saveChanges}
                                  disabled={saveLoading}
                                  className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"
                                >
                                  {saveLoading ? 'Saving...' : 'Save'}
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => startEdit('specialties', doctor.id)}
                                className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-400"
                              >
                                Edit Record
                              </button>
                            )}
                          </div>
                          <div className="p-3">
                            {editingSection === 'specialties' && editingDoctorId === doctor.id ? (
                              <div className="space-y-2">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Specialties (comma-separated)</label>
                                  <textarea
                                    value={editFormData.specialties || ''}
                                    onChange={(e) => updateEditFormData('specialties', e.target.value)}
                                    rows="3"
                                    placeholder="e.g., General Practice, Cardiology, Pediatrics"
                                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                  />
                                </div>
                              </div>
                            ) : (
                              <div className="flex flex-wrap gap-2">
                                {doctor.specialties ? doctor.specialties.split(',').map((specialty, index) => (
                                  <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                                    {specialty.trim()}
                                  </span>
                                )) : (
                                  <span className="text-xs text-gray-500">No specialties listed</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Profile Section */}
                        <div className="bg-white rounded-lg border border-gray-200">
                          <div className={`px-3 py-1.5 flex justify-between items-center ${
                            expandedDoctor === doctor.id 
                              ? 'bg-gradient-to-r from-blue-600 to-blue-700' 
                              : 'bg-blue-600'
                          } text-white`}>
                            <h3 className="text-xs font-medium flex items-center">
                              <Contact className="h-3 w-3 mr-2" />
                              Profile
                            </h3>
                            {editingSection === 'profile' && editingDoctorId === doctor.id ? (
                              <div className="flex gap-2">
                                <button
                                  onClick={saveChanges}
                                  disabled={saveLoading}
                                  className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"
                                >
                                  {saveLoading ? 'Saving...' : 'Save'}
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => startEdit('profile', doctor.id)}
                                className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-400"
                              >
                                Edit Record
                              </button>
                            )}
                          </div>
                          <div className="p-3">
                            {editingSection === 'profile' && editingDoctorId === doctor.id ? (
                              <div className="space-y-2">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Summary</label>
                                  <textarea
                                    value={editFormData.summary || ''}
                                    onChange={(e) => updateEditFormData('summary', e.target.value)}
                                    rows="3"
                                    placeholder="Professional summary and bio"
                                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                  />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Date of Birth</label>
                                    <input
                                      type="date"
                                      value={editFormData.date_of_birth || ''}
                                      onChange={(e) => updateEditFormData('date_of_birth', e.target.value)}
                                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Gender</label>
                                    <select
                                      value={editFormData.gender || ''}
                                      onChange={(e) => updateEditFormData('gender', e.target.value)}
                                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                      <option value="">Select Gender</option>
                                      <option value="male">Male</option>
                                      <option value="female">Female</option>
                                      <option value="other">Other</option>
                                      <option value="prefer_not_to_say">Prefer not to say</option>
                                    </select>
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Languages</label>
                                    <input
                                      type="text"
                                      value={editFormData.languages || ''}
                                      onChange={(e) => updateEditFormData('languages', e.target.value)}
                                      placeholder="e.g., English, Spanish, French"
                                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">License Number</label>
                                    <input
                                      type="text"
                                      value={editFormData.license_number || ''}
                                      onChange={(e) => updateEditFormData('license_number', e.target.value)}
                                      placeholder="Medical license number"
                                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Years of Experience</label>
                                    <input
                                      type="number"
                                      value={editFormData.years_experience || ''}
                                      onChange={(e) => updateEditFormData('years_experience', e.target.value)}
                                      placeholder="Years"
                                      min="0"
                                      max="50"
                                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Verification Status</label>
                                    <select
                                      value={editFormData.verification_status || 'pending'}
                                      onChange={(e) => updateEditFormData('verification_status', e.target.value)}
                                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                      <option value="pending">Pending</option>
                                      <option value="verified">Verified</option>
                                      <option value="rejected">Rejected</option>
                                      <option value="suspended">Suspended</option>
                                    </select>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Summary</label>
                                  <textarea
                                    value={doctor.summary || ''}
                                    readOnly
                                    rows="3"
                                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-gray-50"
                                  />
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  <div>
                                    <span className="font-medium text-gray-700">Date of Birth:</span>
                                    <span className="ml-2 text-gray-600">{doctor.date_of_birth || 'Not specified'}</span>
                                  </div>
                                  <div>
                                    <span className="font-medium text-gray-700">Gender:</span>
                                    <span className="ml-2 text-gray-600 capitalize">{doctor.gender || 'Not specified'}</span>
                                  </div>
                                  <div>
                                    <span className="font-medium text-gray-700">Languages:</span>
                                    <span className="ml-2 text-gray-600">{doctor.languages || 'Not specified'}</span>
                                  </div>
                                  <div>
                                    <span className="font-medium text-gray-700">License #:</span>
                                    <span className="ml-2 text-gray-600">{doctor.license_number || 'Not specified'}</span>
                                  </div>
                                  <div>
                                    <span className="font-medium text-gray-700">Experience:</span>
                                    <span className="ml-2 text-gray-600">{doctor.years_experience ? `${doctor.years_experience} years` : 'Not specified'}</span>
                                  </div>
                                  <div>
                                    <span className="font-medium text-gray-700">Status:</span>
                                    <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                                      doctor.verification_status === 'verified' ? 'bg-green-100 text-green-800' :
                                      doctor.verification_status === 'rejected' ? 'bg-red-100 text-red-800' :
                                      doctor.verification_status === 'suspended' ? 'bg-orange-100 text-orange-800' :
                                      'bg-yellow-100 text-yellow-800'
                                    }`}>
                                      {doctor.verification_status || 'Pending'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* AC Vitals Access Section */}
                        <div className="bg-white rounded-lg border border-gray-200">
                          <div className={`px-3 py-1.5 flex justify-between items-center ${
                            expandedDoctor === doctor.id 
                              ? 'bg-gradient-to-r from-green-50 to-emerald-100 border-b border-green-200 text-green-900'
                              : 'bg-green-600 text-white'
                          }`}>
                            <h3 className="text-xs font-medium flex items-center">
                              <Activity className="h-3 w-3 mr-2" />
                              AC Vitals Monitor Access
                            </h3>
                          </div>
                          <div className="p-3">
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium text-gray-700">Access Status:</span>
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={async () => {
                                      try {
                                        const newStatus = !doctor.ac_vitals_access;
                                        await doctorService.updateAcVitalsAccess(doctor.id, newStatus, null);
                                        await fetchDoctors();
                                        addToast(`AC Vitals access ${newStatus ? 'granted' : 'revoked'} successfully`, 'success');
                                      } catch (error) {
                                        addToast('Failed to update AC Vitals access', 'error');
                                      }
                                    }}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                                      doctor.ac_vitals_access ? 'bg-green-600' : 'bg-gray-200'
                                    }`}
                                  >
                                    <span
                                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                        doctor.ac_vitals_access ? 'translate-x-6' : 'translate-x-1'
                                      }`}
                                    />
                                  </button>
                                  <span className={`text-xs font-medium ${doctor.ac_vitals_access ? 'text-green-600' : 'text-gray-500'}`}>
                                    {doctor.ac_vitals_access ? 'Enabled' : 'Disabled'}
                                  </span>
                                </div>
                              </div>
                              
                              {doctor.ac_vitals_access && (
                                <>
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-medium text-gray-700">Access Duration:</span>
                                    <select
                                      value={doctor.ac_vitals_access_expiry ? 'custom' : 'indefinite'}
                                      onChange={async (e) => {
                                        const duration = e.target.value;
                                        let days = null;
                                        if (duration !== 'indefinite') {
                                          days = parseInt(duration);
                                        }
                                        try {
                                          await doctorService.updateAcVitalsAccess(doctor.id, true, days);
                                          await fetchDoctors();
                                          addToast('Access duration updated successfully', 'success');
                                        } catch (error) {
                                          addToast('Failed to update access duration', 'error');
                                        }
                                      }}
                                      className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                                    >
                                      <option value="indefinite">Indefinite</option>
                                      <option value="5">5 days</option>
                                      <option value="15">15 days</option>
                                      <option value="30">30 days</option>
                                      <option value="60">60 days</option>
                                      <option value="90">90 days</option>
                                    </select>
                                  </div>
                                  
                                  {doctor.ac_vitals_access_expiry && (
                                    <div className="text-xs text-gray-600 bg-yellow-50 border border-yellow-200 rounded p-2">
                                      <span className="font-medium">Expires on:</span> {new Date(doctor.ac_vitals_access_expiry).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Accounts Section */}
                        <div className="bg-white rounded-lg border border-gray-200">
                          <div className={`px-3 py-1.5 flex justify-between items-center ${
                            expandedDoctor === doctor.id 
                              ? 'bg-gradient-to-r from-blue-50 to-indigo-100 border-b border-blue-200 text-blue-900'
                              : 'bg-blue-600 text-white'
                          }`}>
                            <h3 className="text-xs font-medium flex items-center">
                              <Settings className="h-3 w-3 mr-2" />
                              Accounts
                            </h3>
                            {editingSection === 'accounts' && editingDoctorId === doctor.id ? (
                              <div className="flex gap-2">
                                <button
                                  onClick={saveChanges}
                                  disabled={saveLoading}
                                  className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"
                                >
                                  {saveLoading ? 'Saving...' : 'Save'}
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => startEdit('accounts', doctor.id)}
                                className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                              >
                                Edit Record
                              </button>
                            )}
                          </div>
                          <div className="p-3">
                            <div className="grid grid-cols-3 gap-2">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Accounts Email</label>
                                <input
                                  type="email"
                                  value={
                                    editingSection === 'accounts' && editingDoctorId === doctor.id
                                      ? editFormData.accounts_email || ''
                                      : doctor.accounts_email || ''
                                  }
                                  onChange={(e) => editingSection === 'accounts' && editingDoctorId === doctor.id && updateEditFormData('accounts_email', e.target.value)}
                                  readOnly={!(editingSection === 'accounts' && editingDoctorId === doctor.id)}
                                  className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                    editingSection === 'accounts' && editingDoctorId === doctor.id
                                      ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                      : 'bg-gray-50'
                                  }`}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Hourly Rate</label>
                                <input
                                  type="number"
                                  value={
                                    editingSection === 'accounts' && editingDoctorId === doctor.id
                                      ? editFormData.hourly_rate || ''
                                      : doctor.hourly_rate || ''
                                  }
                                  onChange={(e) => editingSection === 'accounts' && editingDoctorId === doctor.id && updateEditFormData('hourly_rate', e.target.value)}
                                  readOnly={!(editingSection === 'accounts' && editingDoctorId === doctor.id)}
                                  className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                    editingSection === 'accounts' && editingDoctorId === doctor.id
                                      ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                      : 'bg-gray-50'
                                  }`}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Appt. Rate</label>
                                <input
                                  type="number"
                                  value={
                                    editingSection === 'accounts' && editingDoctorId === doctor.id
                                      ? editFormData.appt_rate || ''
                                      : doctor.appt_rate || ''
                                  }
                                  onChange={(e) => editingSection === 'accounts' && editingDoctorId === doctor.id && updateEditFormData('appt_rate', e.target.value)}
                                  readOnly={!(editingSection === 'accounts' && editingDoctorId === doctor.id)}
                                  className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                    editingSection === 'accounts' && editingDoctorId === doctor.id
                                      ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                      : 'bg-gray-50'
                                  }`}
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Qualifications & Insurances Section */}
                        <div className="bg-white rounded-lg border border-gray-200">
                          <div className="bg-blue-600 text-white px-3 py-1.5">
                            <h3 className="text-xs font-medium flex items-center">
                              <FileText className="h-3 w-3 mr-2" />
                              Qualifications & Insurances
                            </h3>
                          </div>
                          <div className="p-3">
                            <div className="grid grid-cols-5 gap-3">
                              {getDummyQualifications(doctor.id).map((qualification) => (
                                <div key={qualification.id} className="text-center">
                                  <div className="w-12 h-12 mx-auto bg-red-100 rounded-lg flex items-center justify-center mb-2">
                                    <qualification.icon className="h-6 w-6 text-red-600" />
                                  </div>
                                  <p className="text-xs font-medium text-gray-900 mb-1">{qualification.type}</p>
                                  <p className="text-xs text-gray-500">{qualification.name}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-500">
                  Showing {startIndex + 1}-{Math.min(endIndex, filteredDoctors.length)} of {filteredDoctors.length} doctors
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className="p-2 rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  
                  <div className="flex space-x-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => (
                      <button
                        key={pageNumber}
                        onClick={() => handlePageClick(pageNumber)}
                        className={`px-3 py-2 text-sm rounded-md transition-colors ${
                          currentPage === pageNumber
                            ? 'bg-blue-600 text-white'
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    ))}
                  </div>
                  
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Doctor Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Add New Doctor</h2>
              <button
                onClick={closeAddModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6">
              <form onSubmit={(e) => { e.preventDefault(); handleAddDoctor(); }}>
                {/* Doctor Details */}
                <div className="mb-6">
                  <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                    <User className="h-4 w-4 mr-2" />
                    Doctor Details
                  </h3>
                  
                  {/* First Row */}
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">First Name *</label>
                      <input
                        type="text"
                        value={addDoctorData.firstname}
                        onChange={(e) => updateAddDoctorData('firstname', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Last Name *</label>
                      <input
                        type="text"
                        value={addDoctorData.lastname}
                        onChange={(e) => updateAddDoctorData('lastname', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Doctor Address</label>
                      <input
                        type="text"
                        value={addDoctorData.doctor_address}
                        onChange={(e) => updateAddDoctorData('doctor_address', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Suburb</label>
                      <input
                        type="text"
                        list="add-doctor-suburb-list"
                        value={addDoctorData.suburb}
                        onChange={(e) => updateAddDoctorData('suburb', e.target.value)}
                        placeholder="Type or select suburb"
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <datalist id="add-doctor-suburb-list">
                        {getUniqueSuburbs.map((suburb) => (
                          <option key={suburb} value={suburb} />
                        ))}
                      </datalist>
                    </div>
                  </div>

                  {/* Second Row */}
                  <div className="grid grid-cols-5 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">State</label>
                      <input
                        type="text"
                        list="add-doctor-state-list"
                        value={addDoctorData.state}
                        onChange={(e) => updateAddDoctorData('state', e.target.value)}
                        placeholder="Type or select state"
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <datalist id="add-doctor-state-list">
                        {getUniqueStates.map((state) => (
                          <option key={state} value={state} />
                        ))}
                      </datalist>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Postcode</label>
                      <input
                        type="text"
                        value={addDoctorData.postcode}
                        onChange={(e) => updateAddDoctorData('postcode', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Country</label>
                      <input
                        type="text"
                        list="add-doctor-country-list"
                        value={addDoctorData.country}
                        onChange={(e) => updateAddDoctorData('country', e.target.value)}
                        placeholder="Type or select country"
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <datalist id="add-doctor-country-list">
                        {getUniqueCountries.map((country) => (
                          <option key={country} value={country} />
                        ))}
                      </datalist>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Doctor Phone</label>
                      <input
                        type="text"
                        value={addDoctorData.phone}
                        onChange={(e) => updateAddDoctorData('phone', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Doctor Email</label>
                      <input
                        type="email"
                        value={addDoctorData.email}
                        onChange={(e) => updateAddDoctorData('email', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {/* Third Row */}
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Specialties</label>
                      <input
                        type="text"
                        value={addDoctorData.specialties}
                        onChange={(e) => updateAddDoctorData('specialties', e.target.value)}
                        placeholder="e.g., Cardiology, Internal Medicine"
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Summary</label>
                      <textarea
                        value={addDoctorData.summary}
                        onChange={(e) => updateAddDoctorData('summary', e.target.value)}
                        rows="3"
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Accounts Information */}
                <div className="mb-6">
                  <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                    <Settings className="h-4 w-4 mr-2" />
                    Accounts Information
                  </h3>
                  
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Accounts Email</label>
                      <input
                        type="email"
                        value={addDoctorData.accounts_email}
                        onChange={(e) => updateAddDoctorData('accounts_email', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Hourly Rate</label>
                      <input
                        type="number"
                        step="0.01"
                        value={addDoctorData.hourly_rate}
                        onChange={(e) => updateAddDoctorData('hourly_rate', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Appointment Rate</label>
                      <input
                        type="number"
                        step="0.01"
                        value={addDoctorData.appt_rate}
                        onChange={(e) => updateAddDoctorData('appt_rate', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closeAddModal}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={addLoading || !addDoctorData.firstname || !addDoctorData.lastname || !addDoctorData.email}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {addLoading ? 'Creating...' : 'Create Doctor'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <div className="fixed top-4 right-4 space-y-2 z-50">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`flex items-center px-4 py-3 rounded-lg shadow-lg transform transition-all duration-300 ease-in-out ${
              toast.type === 'success'
                ? 'bg-green-500 text-white'
                : 'bg-red-500 text-white'
            }`}
          >
            {toast.type === 'success' ? (
              <CheckCircle className="h-5 w-5 mr-3 flex-shrink-0" />
            ) : (
              <AlertCircle className="h-5 w-5 mr-3 flex-shrink-0" />
            )}
            <span className="text-sm font-medium">{toast.message}</span>
            <button
              onClick={() => removeToast(toast.id)}
              className="ml-3 flex-shrink-0 text-white hover:text-gray-200 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminDoctorsPage;
